import React, { useEffect } from "react";
import { Modal, Button, ModalHeader, Input, FormFeedback, Label } from "reactstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

const reasonOptions = [
  { value: 12, label: "Legal Issue" },
  { value: 13, label: "Document Issue" },
  { value: 14, label: "Re-check" },
];

const FileCommentModal = ({
  show,
  setShow,
  title,
  onSubmitHandler,
  disableOutsideClick = false,
  size = "md",
  selectedRow = {},
}) => {
  const { t, i18n } = useTranslation();

  // Validation schema
  const validationSchema = Yup.object().shape({
    reason: Yup.number()
      .required(t("Reason is required"))
      .typeError(t("Reason is required")), // Will make sure it's treated as invalid if empty or incorrect
    comment: Yup.string()
      .trim()
      .required(t("Comment is required")),
  });

  return (
    <Modal
      size={size}
      isOpen={show}
      toggle={() => {
        if (!disableOutsideClick) setShow(false);
      }}
    >
      {/* ********************* MODAL HEADER ***************** */}
      <ModalHeader tag="div" className="modalClose">
        {i18n.language === "ar" ? (
          <div className="d-flex justify-content-between align-items-center w-100">
            <button
              type="button"
              className="btn-close ms-2"
              onClick={() => setShow(false)}
              aria-label="Close"
            />
            <h5 className="modal-title mb-0">{title}</h5>
          </div>
        ) : (
          <div className="d-flex justify-content-between align-items-center w-100">
            <h5 className="modal-title mb-0">{title}</h5>
            <button
              type="button"
              className="btn-close ms-2"
              onClick={() => setShow(false)}
              aria-label="Close"
            />
          </div>
        )}
      </ModalHeader>

      {/* ********************* MODAL BODY ***************** */}
      <Formik
        enableReinitialize
        initialValues={{
          reason: [12, 13, 14].includes(selectedRow.Status) ? selectedRow.Status : null,
          comment: selectedRow.Comment || null,
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          onSubmitHandler(values); // pass reason & comment
          setSubmitting(false);
          resetForm();
          setShow(false);
        }}
      >
        {({ errors, touched, isSubmitting, isValid, resetForm, values }) => {
          // Reset form when modal closes
          useEffect(() => {
            if (!show) resetForm();
          }, [show, resetForm]);

          return (
            <Form encType="multipart/form-data">
              <div className="modal-body">
                {/* Reason Dropdown */}
                <div className="mb-3">
                  <Label for="reason" className="form-label">
                    {t("Reason")}
                  </Label>
                  <Field name="reason">
                    {({ field, form }) => (
                      <Input
                        {...field}
                        type="select"
                        id="reason"
                        invalid={touched.reason && !!errors.reason}
                        onChange={(e) =>
                          form.setFieldValue(field.name, e.target.value)
                        }
                      >
                        <option value="">{t("Select reason")}</option>
                        {reasonOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Input>
                    )}
                  </Field>
                  {touched.reason && errors.reason && (
                    <FormFeedback>{errors.reason}</FormFeedback>
                  )}
                </div>

                {/* Comment Field */}
                <div>
                  <Label for="comment" className="form-label">
                    {t("Comment")}
                  </Label>
                  <Field name="comment">
                    {({ field }) => (
                      <Input
                        {...field}
                        type="textarea"
                        rows={5}
                        placeholder={t("Enter your comment")}
                        invalid={touched.comment && !!errors.comment}
                      />
                    )}
                  </Field>
                  {touched.comment && errors.comment && (
                    <FormFeedback>{errors.comment}</FormFeedback>
                  )}
                </div>
              </div>

              {/* MODAL FOOTER */}
              <div className="modal-footer d-flex justify-content-center gap-4">
                <Link
                  to="#"
                  className="btn btn-link link-success fw-medium"
                  onClick={() => setShow(false)}
                >
                  <i className="ri-close-line me-1 align-middle"></i> {t("Close")}
                </Link>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting || !isValid || !values.reason}
                >
                  <i className="ri-save-3-fill align-bottom me-2"></i>
                  {t("Save Changes")}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default FileCommentModal;